import { supabase } from '@/config/supabase'
import {
  ZONE_QUOTAZIONI,
  COEFFICIENTI_EXTRA,
  type ZoneQuotazione,
} from '@/data/zone-quotazioni'

// Cache in memoria con TTL per evitare fetch ripetuti ma garantire dati freschi
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minuti
const NEGATIVE_CACHE_TTL_MS = 30 * 1000 // 30s se fallback usato

let cachedZones: ZoneQuotazione[] | null = null
let cachedZonesTimestamp = 0
let cachedCoefficienti: typeof COEFFICIENTI_EXTRA | null = null
let cachedCoefficientiTimestamp = 0

function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL_MS
}

function safeNumber(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

export async function fetchZoneQuotazioni(): Promise<ZoneQuotazione[]> {
  if (cachedZones && isCacheValid(cachedZonesTimestamp)) return cachedZones

  try {
    const { data, error } = await supabase
      .from('zone_quotazioni_cagliari')
      .select('*')
      .order('nome')

    if (error || !data || data.length === 0) {
      console.error('[zone-quotazioni] fetch fallback hardcoded:', error?.message || 'empty')
      cachedZones = ZONE_QUOTAZIONI
      cachedZonesTimestamp = Date.now() - (CACHE_TTL_MS - NEGATIVE_CACHE_TTL_MS)
      return cachedZones
    }

    cachedZones = data.map((row) => ({
      nome: row.nome,
      codiceOMI: row.codice_omi || '',
      prezzoAbitabile: safeNumber(row.prezzo_abitabile, 0),
      prezzoRistrutturato: safeNumber(row.prezzo_ristrutturato, 0),
      prezzoDaRistrutturare: safeNumber(row.prezzo_da_ristrutturare, 0),
      posizioneBonus: safeNumber(row.posizione_bonus, 0),
      pertinenzaBonus: safeNumber(row.pertinenza_bonus, 0),
      esposizioneBonus: safeNumber(row.esposizione_bonus, 0),
      spaziEsterniBonus: safeNumber(row.spazi_esterni_bonus, 0),
      senzaAscensore: safeNumber(row.senza_ascensore, 0),
      casa: safeNumber(row.casa, 1),
      villa: safeNumber(row.villa, 1),
    }))
    cachedZonesTimestamp = Date.now()
    return cachedZones
  } catch (err) {
    console.error('[zone-quotazioni] fetch exception:', err)
    cachedZones = ZONE_QUOTAZIONI
    cachedZonesTimestamp = Date.now() - (CACHE_TTL_MS - NEGATIVE_CACHE_TTL_MS)
    return cachedZones
  }
}

export async function fetchCoefficientiExtra(): Promise<typeof COEFFICIENTI_EXTRA> {
  if (cachedCoefficienti && isCacheValid(cachedCoefficientiTimestamp)) return cachedCoefficienti

  try {
    const { data, error } = await supabase
      .from('coefficienti_extra_cagliari')
      .select('*')

    if (error || !data || data.length === 0) {
      console.error('[coefficienti-extra] fetch fallback hardcoded:', error?.message || 'empty')
      cachedCoefficienti = { ...COEFFICIENTI_EXTRA }
      cachedCoefficientiTimestamp = Date.now() - (CACHE_TTL_MS - NEGATIVE_CACHE_TTL_MS)
      return cachedCoefficienti
    }

    const result = { ...COEFFICIENTI_EXTRA }
    data.forEach((row) => {
      if (row.key in result) {
        ;(result as Record<string, number>)[row.key] = safeNumber(row.value, (result as Record<string, number>)[row.key])
      } else {
        console.warn(`[coefficienti-extra] key sconosciuta ignorata: ${row.key}`)
      }
    })
    cachedCoefficienti = result
    cachedCoefficientiTimestamp = Date.now()
    return cachedCoefficienti
  } catch (err) {
    console.error('[coefficienti-extra] fetch exception:', err)
    cachedCoefficienti = { ...COEFFICIENTI_EXTRA }
    cachedCoefficientiTimestamp = Date.now() - (CACHE_TTL_MS - NEGATIVE_CACHE_TTL_MS)
    return cachedCoefficienti
  }
}

// Salva tutte le zone su Supabase (upsert per nome)
export async function saveZoneQuotazioni(zones: ZoneQuotazione[]): Promise<void> {
  const rows = zones.map((z) => ({
    nome: z.nome,
    codice_omi: z.codiceOMI,
    prezzo_abitabile: z.prezzoAbitabile,
    prezzo_ristrutturato: z.prezzoRistrutturato,
    prezzo_da_ristrutturare: z.prezzoDaRistrutturare,
    posizione_bonus: z.posizioneBonus,
    pertinenza_bonus: z.pertinenzaBonus,
    esposizione_bonus: z.esposizioneBonus,
    spazi_esterni_bonus: z.spaziEsterniBonus,
    senza_ascensore: z.senzaAscensore,
    casa: z.casa,
    villa: z.villa,
    updated_at: new Date().toISOString(),
  }))

  const { error } = await supabase
    .from('zone_quotazioni_cagliari')
    .upsert(rows, { onConflict: 'nome' })

  if (error) throw error
  cachedZones = null
}

// Salva coefficienti extra su Supabase (upsert per key)
export async function saveCoefficientiExtra(
  coefficienti: typeof COEFFICIENTI_EXTRA
): Promise<void> {
  const rows = Object.entries(coefficienti).map(([key, value]) => ({
    key,
    value,
    updated_at: new Date().toISOString(),
  }))

  const { error } = await supabase
    .from('coefficienti_extra_cagliari')
    .upsert(rows, { onConflict: 'key' })

  if (error) throw error
  cachedCoefficienti = null
}

// Elimina zona da Supabase
export async function deleteZone(zoneName: string): Promise<void> {
  const { error } = await supabase
    .from('zone_quotazioni_cagliari')
    .delete()
    .eq('nome', zoneName)

  if (error) throw error
  cachedZones = null
}

// Recupera timestamp ultima modifica delle zone
export async function fetchLastUpdatedAt(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('zone_quotazioni_cagliari')
      .select('updated_at')
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()

    if (error || !data) return null
    return data.updated_at
  } catch {
    return null
  }
}

// Invalida cache (utile dopo salvataggio admin)
export function invalidateCache() {
  cachedZones = null
  cachedZonesTimestamp = 0
  cachedCoefficienti = null
  cachedCoefficientiTimestamp = 0
}
